import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { titleChipHtml } from '../components/Title.js';
import { craftBoxHtml, craftProgress, CRAFT_BOX_CSS } from '../util/craft.js';
import type { InventoryItemDto, OccupantDto, CharacterDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

/** Slugs of items whose effect removes Infirmary. Mirror of the catalog flag. */
const REVIVE_SLUGS = new Set(['friars_aid']);

let countdownTimer: number | null = null;

function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

export async function renderInfirmary(root: HTMLElement): Promise<void> {
  // Refresh state on entry.
  let inventory: InventoryItemDto[] = [];
  let occupants: OccupantDto[] = [];
  let reviveVigorCost = 150;
  let leechcraftLevel = 0;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const inv = await api.listInventory();
    inventory = inv.items;
    const occ = await api.listInfirmaryOccupants();
    occupants = occ.occupants;
    reviveVigorCost = occ.reviveVigorCost;
    leechcraftLevel = occ.leechcraftLevel;
  } catch {
    // tolerated
  }

  let flash: FlashState | null = null;

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      const inInfirmary = char.status === 'INFIRMARY';
      const medicalActive =
        !!char.medicalCooldownUntil &&
        Date.parse(char.medicalCooldownUntil) > Date.now();

      const revives = inventory.filter((it) => REVIVE_SLUGS.has(it.slug));

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/infirmary.webp" alt="" />
        <h1>The Infirmary</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          The friars are kind. The bills, less so.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <h2>${inInfirmary ? 'You are wounded' : 'You are well'}</h2>
          <dl class="kv">
            <dt>Status</dt>
            <dd id="statline">${
              inInfirmary
                ? `In the Infirmary — <span id="cd">${formatRemaining(char.statusUntil)}</span>`
                : 'Hale and unhurt.'
            }</dd>
            <dt>Life</dt><dd>${char.life} / ${char.lifeMax}</dd>
            <dt>Medical cooldown</dt>
            <dd>${
              medicalActive
                ? `active until ${new Date(char.medicalCooldownUntil!).toLocaleTimeString()}`
                : 'none'
            }</dd>
          </dl>
        </div>

        ${
          inInfirmary
            ? `
              <div class="panel">
                <h2>Revives in your packs</h2>
                ${
                  revives.length === 0
                    ? `<p class="empty" style="font-style:italic;color:var(--c-ink-soft)">
                         No revives. The friars work at their own pace, or you wait it out.
                       </p>`
                    : `<div id="revives" class="item-grid"></div>`
                }
              </div>`
            : ''
        }

        <div class="panel">
          <h2>The Other Patients</h2>
          <p style="color: var(--c-ink-soft); font-style: italic; margin-top: -4px;">
            Pour thy own vigor into a wounded soul to put them back on their feet —
            it costs <strong>${reviveVigorCost} vigor</strong>. This is the
            <strong>Leechcraft</strong> — and the more souls thou mendest, the less it
            takes from thee (down to 20 vigor at mastery).
          </p>
          ${craftBoxHtml(
            'Leechcraft',
            'Each revive earns XP and lowers the next revive&rsquo;s Vigor cost.',
            leechcraftLevel,
          )}
          ${
            char.status !== 'OKAY'
              ? `<p class="alert danger">Thou must be hale and free thyself before thou canst revive another.</p>`
              : ''
          }
          ${
            occupants.length === 0
              ? `<p class="empty" style="font-style:italic;color:var(--c-ink-soft)">The Infirmary beds lie empty. None are wounded this hour.</p>`
              : `<div id="occupants" class="occupant-list">${occupantRowsHtml(occupants, char, reviveVigorCost)}</div>`
          }
        </div>
      `;

      // Reuse Inventory styles for consistency.
      const style = document.createElement('style');
      style.textContent = `
        .item-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: var(--s-3);
        }
        .item-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
        }
        .item-card .row {
          display: flex; justify-content: space-between; align-items: baseline;
        }
        .item-card h3 { margin: 0; font-size: 1rem; }
        .item-card .qty {
          font-family: var(--font-numeric);
          color: var(--c-oxblood);
          font-weight: bold;
        }
        .item-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.88rem;
          margin: 4px 0 8px 0;
        }
        .occupant-list {
          display: flex;
          flex-direction: column;
          gap: var(--s-2);
        }
        .occupant-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--s-3);
          padding: var(--s-2) var(--s-3);
          background: var(--c-parchment);
          border: 1px solid var(--c-rule-soft);
          border-radius: var(--radius);
        }
        .occupant-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .occupant-name {
          font-family: var(--font-display);
          font-weight: 600;
          color: var(--c-ink);
        }
        .occupant-meta {
          font-family: var(--font-numeric);
          font-size: 0.82rem;
          color: var(--c-ink-soft);
        }
        .occupant-row .btn { flex: 0 0 auto; }
        /* Mobile: the long "Revive (−35% vigor & life)" label can't shrink and
           was overlapping the patient name/level. Stack the row so the button
           drops below the info and spans the full width. */
        @media (max-width: 560px) {
          .occupant-row {
            flex-direction: column;
            align-items: stretch;
          }
          .occupant-row .btn { width: 100%; }
        }
        .craft-box { margin-bottom: var(--s-3); }
        ${CRAFT_BOX_CSS}
      `;
      main.appendChild(style);

      // Flash.
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Render revive cards.
      const grid = main.querySelector<HTMLDivElement>('#revives');
      if (grid && inInfirmary) {
        for (const it of revives) {
          const card = document.createElement('div');
          card.className = 'item-card';
          card.innerHTML = `
            <div class="row">
              <h3>${escapeHtml(it.name)}</h3>
              <span class="qty">×${it.qty}</span>
            </div>
            <p class="blurb">${escapeHtml(it.blurb)}</p>
            <button class="btn" data-use="${it.slug}" ${medicalActive ? 'disabled' : ''}>
              ${medicalActive ? 'Medical cooldown active' : 'Use'}
            </button>
          `;
          grid.appendChild(card);
        }
        grid.querySelectorAll<HTMLButtonElement>('button[data-use]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const slug = btn.dataset.use!;
            btn.disabled = true;
            try {
              const r = await api.useItem({ itemSlug: slug });
              store.set({ character: r.character });
              const inv = await api.listInventory();
              inventory = inv.items;
              if (r.overdosed) {
                flash = {
                  kind: 'danger',
                  message: `Overdose! ${r.itemName} did nothing but put you deeper in the Infirmary.`,
                };
              } else if (r.removedStatus === 'INFIRMARY') {
                flash = {
                  kind: 'success',
                  message: `${r.itemName} sees you back on your feet.`,
                };
              } else {
                flash = {
                  kind: 'success',
                  message: `${r.itemName} used.`,
                };
              }
            } catch (err) {
              const msg = err instanceof HttpError ? err.message : 'Use failed.';
              flash = { kind: 'danger', message: msg };
            } finally {
              anchored(renderMain);
            }
          });
        });
      }

      // Revive buttons — pour thy own vigor + life into another patient.
      main.querySelectorAll<HTMLButtonElement>('button[data-revive]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          if (btn.disabled) return;
          const targetId = btn.dataset.revive!;
          const targetName = btn.dataset.name ?? 'this patient';
          const ok = await showConfirmDialog({
            title: `Revive ${targetName}?`,
            message: `This pours ${reviveVigorCost} vigor from you into them. Each revive sharpens thy Leechcraft and lowers the cost of the next.`,
            confirmLabel: 'Yes, revive them',
            cancelLabel: 'Cancel',
            tone: 'gold',
          });
          if (!ok) return;
          btn.disabled = true;
          try {
            const r = await api.revivePatient(targetId);
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `${r.revivedName} stirs, and stands. (−${r.vigorSpent} vigor · Leechcraft now level ${craftProgress(r.leechcraftLevel).level} / 10 · ${r.leechcraftLevel} souls revived)`,
            };
            const occ = await api.listInfirmaryOccupants();
            occupants = occ.occupants;
            reviveVigorCost = occ.reviveVigorCost;
            leechcraftLevel = occ.leechcraftLevel;
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Revive failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Live countdown for the Infirmary timer.
      clearCountdown();
      if (inInfirmary && char.statusUntil) {
        countdownTimer = window.setInterval(() => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          if (!cd) {
            clearCountdown();
            return;
          }
          const remaining = formatRemaining(char.statusUntil);
          cd.textContent = remaining;
          if (remaining === '0s') {
            clearCountdown();
            // Refresh state — the lazy regen on the server will clear status on next /me.
            void api.me().then((m) => {
              store.set({ account: m.account, character: m.character });
              anchored(renderMain);
            });
          }
        }, 1000);
      }
    };

    anchored(renderMain);
  });

  // Cleanup the timer when navigating away.
  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function formatRemaining(iso: string | null): string {
  if (!iso) return '0s';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/**
 * Render the patient rows for "The Other Patients" panel. Each row's revive
 * button is disabled (with a reason) when the viewer can't act — they're
 * wounded/held themselves, or lack the flat vigor cost. The vigor cost falls
 * with the viewer's Leechcraft level (server-authoritative; this is a UX hint).
 */
function occupantRowsHtml(
  list: OccupantDto[],
  char: CharacterDto,
  vigorCost: number,
): string {
  const canAct = char.status === 'OKAY';
  return list
    .map((o) => {
      const isSelf = o.characterId === char.id;
      const strongEnough = char.vigor >= vigorCost;
      const canRevive = canAct && !isSelf && strongEnough;
      const reason = isSelf
        ? 'This is you'
        : !canAct
        ? 'Heal yourself first'
        : !strongEnough
        ? `Too weak — need ${vigorCost} vigor`
        : `Pour ${vigorCost} vigor into them`;
      return `
        <div class="occupant-row">
          <div class="occupant-id">
            <span class="occupant-name"><a href="#/u/${encodeURIComponent(o.characterId)}">${escapeHtml(o.name)}</a>${titleChipHtml(o.activeTitleSlug)}</span>
            <span class="occupant-meta">Level ${o.level} · heals in ${formatRemaining(o.statusUntil)}</span>
          </div>
          <button
            class="btn ${canRevive ? '' : 'ghost'}"
            data-revive="${o.characterId}"
            data-name="${escapeHtml(o.name)}"
            title="${escapeHtml(reason)}"
            ${canRevive ? '' : 'disabled'}
          >
            Revive (−${vigorCost} vigor)
          </button>
        </div>`;
    })
    .join('');
}

